import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Card
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';
import StyledDataGrid from '../components/StyledDataGrid';

const AuditLogs = ({ access = 'none' }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);
  const [stats, setStats] = useState(null);

  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    user_id: '',
    date_from: '',
    date_to: ''
  });

  const canRead = access === 'admin';

  useEffect(() => {
    if (canRead) {
      fetchLogs();
      fetchStats();
    }
  }, [canRead, page, rowsPerPage, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString()
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(apiUrl(`/api/audit-logs?${params}`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalLogs(data.pagination?.total || 0);
      } else {
        throw new Error('Failed to fetch audit logs');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(apiUrl('/api/audit-logs/stats'), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage - 1); // DataGrid sends 1-indexed, we store 0-indexed
  };

  const handleChangeRowsPerPage = (newPageSize) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      case 'LOGIN':
        return 'primary';
      case 'LOGOUT':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: 'user_name',
      headerName: 'User',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 0.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
            {params.value || 'Unknown'}
          </Typography>
          {params.row.user_email && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
              {params.row.user_email}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getActionColor(params.value)}
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500 }}
        />
      )
    },
    {
      field: 'entity_type',
      headerName: 'Entity Type',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'field_name',
      headerName: 'Field',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'old_value',
      headerName: 'Old Value',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'new_value',
      headerName: 'New Value',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    }
  ];

  if (!canRead) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Alert severity="warning">
          You don't have permission to view audit logs. This feature is only available to Global Administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <LoadingOverlay loading={loading} size={50} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <IconButton onClick={fetchLogs} color="primary" title="Refresh" size="large">
          <RefreshIcon />
        </IconButton>
      </Box>

      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3, flexShrink: 0 }}>
          <Box>
            <Card sx={{
              p: 2.5,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'scale(1.03)' }
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Logs
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {stats.total_logs?.toLocaleString() || 0}
              </Typography>
            </Card>
          </Box>
          <Box>
            <Card sx={{
              p: 2.5,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'scale(1.03)' }
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Last 24 Hours
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {stats.logs_last_24h?.toLocaleString() || 0}
              </Typography>
            </Card>
          </Box>
          <Box>
            <Card sx={{
              p: 2.5,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'scale(1.03)' }
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon color="success" />
                <Typography variant="subtitle2" color="text.secondary">
                  Last 7 Days
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {stats.logs_last_7d?.toLocaleString() || 0}
              </Typography>
            </Card>
          </Box>
          <Box>
            <Card sx={{
              p: 2.5,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'scale(1.03)' }
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon color="secondary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Most Active User
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={600} noWrap title={stats.top_users?.[0]?.user_name}>
                {stats.top_users?.[0]?.user_name || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.top_users?.[0]?.count || 0} actions
              </Typography>
            </Card>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Filters
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <TextField
              select
              label="Entity Type"
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Company">Company</MenuItem>
              <MenuItem value="Project">Project</MenuItem>
              <MenuItem value="DesignChange">Design Change</MenuItem>
              <MenuItem value="DocumentTemplate">Document Template</MenuItem>
              <MenuItem value="CompanyDocument">Company Document</MenuItem>
            </TextField>
            <TextField
              select
              label="Action"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="CREATE">Create</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
              <MenuItem value="LOGIN">Login</MenuItem>
              <MenuItem value="LOGOUT">Logout</MenuItem>
            </TextField>
            <TextField
              label="Date From"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Date To"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Audit Logs DataGrid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={logs}
          columns={columns}
          getRowId={(row) => row.log_id}
          loading={loading}
          paginationMode="server"
          totalRows={totalLogs}
          page={page + 1}
          pageSize={rowsPerPage}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangeRowsPerPage}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default AuditLogs;
